import React, { useEffect, useState, useRef, Fragment, memo } from 'react';
import { ArrowRightIcon, ClockIcon, HandIcon, LeafIcon, MapPinIcon, PackageIcon, ShieldCheckIcon, WindIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ColeccionesTabs } from '../components/ColeccionesTabs';
import { QuizOverlay } from '../components/QuizOverlay';
import { useCart } from '../components/CartContext';
type AromaVariantMap = Record<string, number>;
const FRAGMENTOS_VARIANT_IDS: AromaVariantMap = {
  'Tierra · Sándalo + Palo Santo': 44232167293031,
  'Aire · Bergamota + Té verde': 44232167686247,
  'Agua · Acorde marino + Eucalipto': 44232168046695,
  'Hogar · Vainilla + Almendra': 44232168964199,
  'Recuerdo · Manzanilla + Algodón': 44232169881703,
  'Latir · Tulipán + Vainilla suave': 44232170700903,
  'Ritual · Palo Santo + Lavanda': 44232171552871,
  'Descanso · Manzanilla + Lavanda': 44232172175463,
  'Silencio · Algodón + Té negro': 44232172961895
};
const RITUAL_SAVIA_VARIANT_IDS: AromaVariantMap = {
  'Tierra · Sándalo + Palo Santo': 44232175157351,
  'Aire · Bergamota + Té verde': 44232177352807,
  'Agua · Acorde marino + Eucalipto': 44232182300775,
  'Hogar · Vainilla + Almendra': 44232184103015,
  'Recuerdo · Manzanilla + Algodón': 44232185282663,
  'Latir · Tulipán + Vainilla suave': 44232186167399,
  'Ritual · Palo Santo + Lavanda': 44232187674727,
  'Descanso · Manzanilla + Lavanda': 44232188854375,
  'Silencio · Algodón + Té negro': 44232190034023
};
export function Home() {
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('inicio');
  const [activeIntro, setActiveIntro] = useState<'origen' | 'memoria' | 'pausa' | null>(null);
  const [fragmentosAroma, setFragmentosAroma] = useState('');
  const [fragmentosAromaError, setFragmentosAromaError] = useState(false);
  const [ritualAroma, setRitualAroma] = useState('');
  const [ritualAromaError, setRitualAromaError] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const quizTriggerRef = useRef<HTMLButtonElement>(null);
  const {
    addItem
  } = useCart();
  const closeQuiz = () => {
    setIsQuizOpen(false);
    window.setTimeout(() => quizTriggerRef.current?.focus(), 0);
  };
  const addFragmentos = () => {
    const variantId = FRAGMENTOS_VARIANT_IDS[fragmentosAroma];
    if (!variantId) {
      setFragmentosAromaError(true);
      document.getElementById('fragmentos-aroma')?.focus();
      return;
    }
    addItem({
      variantId,
      name: 'Fragmentos Aromáticos',
      aroma: fragmentosAroma,
      format: 'Pack x10 · 130g aprox.',
      price: 42000,
      image: 'https://cdn.magicpatterns.com/uploads/hfNXz1KRRNk79LtbWbQMLS/FRAGMENTOS.png'
    });
  };
  const addRitualSavia = () => {
    const variantId = RITUAL_SAVIA_VARIANT_IDS[ritualAroma];
    if (!variantId) {
      setRitualAromaError(true);
      document.getElementById('ritual-savia-aroma')?.focus();
      return;
    }
    addItem({
      variantId,
      name: 'Ritual SAVIA',
      aroma: ritualAroma,
      format: 'Pebetero + Pack x10 fragmentos',
      price: 98000,
      image: 'https://cdn.magicpatterns.com/uploads/cmLn7L7Ls22KEHrjLjDVm7/ChatGPT_Image_11_jul_2026,_00_05_12.png'
    });
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });
    const reobserve = () => {
      document.querySelectorAll('.reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible)').forEach(el => observer.observe(el));
    };
    reobserve();
    const mo = new MutationObserver(() => reobserve());
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    const onScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pct = maxScroll > 0 ? scrolled / maxScroll * 100 : 0;
      setProgress(pct);
      const sectionIds = ['inicio', 'colecciones', 'mas-alla', 'nosotros'];
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight * 0.92) {
        setActiveSection('nosotros');
        return;
      }
      const threshold = window.scrollY + window.innerHeight * 0.35;
      let current = sectionIds[0];
      sectionIds.forEach(id => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= threshold) current = id;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => {
      observer.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  const scrollToSection = (id: string, extraOffset = 0) => {
    const target = document.getElementById(id);
    if (!target) return;
    const nav = document.getElementById('nav');
    const offset = (nav?.offsetHeight ?? 0) + 24;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - offset + extraOffset,
      behavior: 'smooth'
    });
  };
  const handleCollectionPillClick = (tab: 'origen' | 'memoria' | 'pausa') => {
    try {
      window.sessionStorage.setItem('savia:pendingTab', tab);
    } catch {
      // Continue when storage is unavailable.
    }
    window.dispatchEvent(new CustomEvent('savia:tab', {
      detail: tab
    }));
    scrollToSection('colecciones');
  };
  return <>
      <div className={`scroll-progress ${progress > 0.5 ? 'active' : ''}`} id="scrollProgress" style={{
      clipPath: `inset(0 ${(100 - progress).toFixed(3)}% 0 0)`,
      '--progress-pct': `${progress}%`
    } as React.CSSProperties} data-id="element-341" data-mp="2015q5o7gdpi0"></div>

      <nav className="section-nav" id="sectionNav" aria-label="Sección activa" data-id="element-342" data-mp="3kkizfmig5dyk">
        <div className={`section-nav__item ${activeSection === 'inicio' ? 'active' : ''}`} data-section="inicio" data-id="element-343" data-mp="3pikr2vtxulln">
          <span className="section-nav__label" data-id="element-344" data-mp="3floh0loe5rez">Inicio</span>
        </div>
        <div className={`section-nav__item ${activeSection === 'colecciones' ? 'active' : ''}`} data-section="colecciones" data-id="element-345" data-mp="3fmzlyotkp57o">
          <span className="section-nav__label" data-id="element-346" data-mp="36k86mdxktpar">Colecciones</span>
        </div>

        <div className={`section-nav__item ${activeSection === 'mas-alla' ? 'active' : ''}`} data-section="mas-alla" data-id="element-347" data-mp="zutk78dtzq5c">
          <span className="section-nav__label" data-id="element-348" data-mp="14t4ufk81g2o3">Kits & Objetos</span>
        </div>
        <div className={`section-nav__item ${activeSection === 'nosotros' ? 'active' : ''}`} data-section="nosotros" data-id="element-349" data-mp="1eymy7inmu0r1">
          <span className="section-nav__label" data-id="element-350" data-mp="19wns410icpqe">Nosotros</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero" id="inicio" data-id="element-351" data-mp="1nrq3v4s1d9b4">
        <div className="hero__visual" data-id="element-352" data-mp="1iy42w9v8itv5">
          <img src="https://cdn.magicpatterns.com/uploads/3EcUbgfEAQtgcc8aT1UsWJ/ChatGPT_Image_11_jul_2026,_12_56_49.png" alt="Vela SAVIA encendida junto a una ventana con luz natural" style={{
          objectPosition: 'center 63%'
        }} data-id="element-353" data-mp="1ix2gj98rqky4" />
          <div className="hero__visual-overlay" data-id="element-354" data-mp="26mn98juq4wkl"></div>
        </div>
        <div className="hero__content" data-id="element-355" data-mp="22oh8bsgkhb7l">
          <div className="hero__content-inner" data-id="element-356" data-mp="22lbg44cn5t7n">
            <p className="hero__eyebrow reveal d1" data-id="element-357" data-mp="cbrv720rspaf">
              SAVIA · Rituales aromáticos
            </p>
            <h1 className="hero__title reveal d2" data-id="element-358" data-mp="8ag7x35dcg5e">
              Habita el aroma que te <em data-id="element-359" data-mp="wuuy3wieobug">devuelve a ti.</em>
            </h1>
            <p className="hero__subtitle reveal d3" data-id="element-360" data-mp="wryz04utnh4f">
              Velas de soya hechas a mano para acompañar tus pausas, tus
              recuerdos y la manera en que eliges habitar tu espacio.
            </p>
            <div className="hero__cta-area reveal d3" data-id="element-361" data-mp="1algkhv72j3x3">
              <div className="hero__actions" data-id="element-362" data-mp="1ak6mt744nxqz">
                <button type="button" className="hero__cta hero__cta--collections" onClick={() => scrollToSection('colecciones')} data-id="element-363" data-mp="dv7clmjzglw5">
                  Explorar colecciones
                  <ArrowRightIcon size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-364" data-mp="mv44dx1o60vf" />
                </button>
                <div className="hero__quiz-cta-wrap" data-id="element-365" data-mp="1ybu2stzupoeh">
                  <span className="hero__quiz-badge" data-id="element-366" data-mp="1ydexqkq2v9cv">
                    Nuevo · Quiz de aroma
                  </span>
                  <button ref={quizTriggerRef} type="button" className="hero__cta hero__cta--quiz" onClick={() => setIsQuizOpen(true)} aria-haspopup="dialog" aria-expanded={isQuizOpen} data-id="element-367" data-mp="2ddssdfz69orc">
                    <span className="hero__quiz-spark" aria-hidden="true" data-id="element-368" data-mp="1zlme5zfkxruj">
                      ✦
                    </span>
                    Encontrar mi aroma
                  </button>
                </div>
              </div>
              <p className="hero__quiz-note" data-id="element-369" data-mp="1pn6kbop644hc">
                Descubre tu aroma en menos de 2 minutos
              </p>
            </div>
            <div className="hero__collections reveal d4" data-id="element-370" data-mp="1go318kq03p2u">
              <p className="hero__collections-label" data-id="element-371" data-mp="1gpmiadjhe45o">Elige tu momento</p>
              <div className="hero__pills" aria-label="Colecciones SAVIA" data-id="element-372" data-mp="3f2ehrfipgqxm">
                <button type="button" className="hero__pill" onClick={() => handleCollectionPillClick('origen')} data-id="element-373" data-mp="3b1b1wt0nquwe">
                  Origen
                </button>
                <button type="button" className="hero__pill" onClick={() => handleCollectionPillClick('memoria')} data-id="element-374" data-mp="8m8647icjzpd">
                  Memoria
                </button>
                <button type="button" className="hero__pill" onClick={() => handleCollectionPillClick('pausa')} data-id="element-375" data-mp="hlv8aviwh9jh">
                  Pausa
                </button>
              </div>
            </div>
            <button type="button" className="hero__kit-link reveal d4" onClick={() => scrollToSection('mas-alla', 120)} data-id="element-376" data-mp="3phnbcf5qq09">
              ¿Buscas un regalo? Conoce el Kit Ritual
              <ArrowRightIcon size={13} strokeWidth={1.5} aria-hidden="true" data-id="element-377" data-mp="37angvwu4vf4u" />
            </button>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="manifesto" data-id="element-378" data-mp="1hubfflj1dtsq">
        <div className="savia-container--narrow" data-id="element-379" data-mp="1mmkzikxgzzn3">
          <div className="manifesto__inner reveal" data-id="element-380" data-mp="1mncsdnzonj9o">
            <p className="manifesto__overline" data-id="element-381" data-mp="18vg640kp43l7">Bogotá · Hecho a mano · 2025</p>
            <p className="manifesto__quote" data-id="element-382" data-mp="18td5ynptwu2r">
              "Aromas que acompañan la forma de habitar."
            </p>
            <div className="manifesto__line" data-id="element-383" data-mp="20mlzzz9tp521"></div>
            <p className="manifesto__tagline" data-id="element-384" data-mp="20mvp7kn1n86o">
              Hecho para habitar en silencio · SAVIA
            </p>
          </div>
        </div>
      </section>

      {/* INTROS DE COLECCIÓN */}
      <section className={`coleccion-intro ${activeIntro === 'origen' ? 'activa' : ''}`} id="intro-origen" data-id="element-385" data-mp="1rkwbb17bsk7y">
        <div className="coleccion-intro__inner" data-id="element-386" data-mp="1wmll6ditarug">
          <div className="coleccion-intro__texto" data-id="element-387" data-mp="g5hcdrvk3ia7">
            <h2 className="coleccion-intro__nombre" data-id="element-388" data-mp="g8vfj2c1nvip">Origen</h2>
            <p className="coleccion-intro__frase" data-id="element-389" data-mp="l76q5fa5ya1z">
              Para volver a lo que siempre fuiste.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-390" data-mp="2mtzxjc05i7h9">
              Hay momentos en que el mundo exige demasiado y lo único que
              necesitas es recordar que tienes raíces. Origen nació para eso —
              para devolverte al centro, al silencio, a lo esencial. Tres aromas
              que huelen a tierra firme, a aire limpio, a empezar de nuevo.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-391" data-mp="2453234t0p8xg">
              <div className="coleccion-intro__aroma" data-id="element-392" data-mp="1z5gk6i28v4ib">
                <span className="aroma-nombre" data-id="element-393" data-mp="1z3vqeunr7pli">Tierra</span>
                <span className="aroma-notas" data-id="element-394" data-mp="1u5c42t590bsm">Sándalo · Palo Santo</span>
                <span className="aroma-desc" data-id="element-395" data-mp="1q4ihq47gf51u">
                  Madera antigua, presencia serena. Para cuando necesitas
                  sentirte enraizado.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-396" data-mp="3kijnl91f8sbf">
                <span className="aroma-nombre" data-id="element-397" data-mp="3kj1gpwsseqf4">Aire</span>
                <span className="aroma-notas" data-id="element-398" data-mp="16badfco4hlrv">Bergamota · Té verde</span>
                <span className="aroma-desc" data-id="element-399" data-mp="168xvv0ma1dfc">
                  Claridad verde, mente despejada. Para cuando quieres pensar
                  sin ruido.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-400" data-mp="1g2xzey1wualy">
                <span className="aroma-nombre" data-id="element-401" data-mp="1k0vom0vokwpc">Agua</span>
                <span className="aroma-notas" data-id="element-402" data-mp="nbwiosikk70b">Acorde marino · Eucalipto</span>
                <span className="aroma-desc" data-id="element-403" data-mp="ndzc7mixki9s">
                  Frescura abierta, respiro limpio. Para soltar lo que ya no
                  necesitas cargar.
                </span>
              </div>
            </div>
          </div>
          <div className="coleccion-intro__visual coleccion-intro__visual--origen" data-id="element-404" data-mp="11c77zr48ddjd">
            <img className="coleccion-intro__image" src="https://cdn.magicpatterns.com/uploads/h4xhXLG28mJdfS1A11ueKj/ChatGPT_Image_11_jul_2026,_11_28_12.png" alt="Vela Tierra SAVIA de la colección Origen" data-id="element-405" data-mp="27rrlhcezui3g" />
          </div>
        </div>
      </section>

      <section className={`coleccion-intro coleccion-intro--memoria ${activeIntro === 'memoria' ? 'activa' : ''}`} id="intro-memoria" data-id="element-406" data-mp="26audh8ookwo2">
        <div className="coleccion-intro__inner coleccion-intro__inner--reverse" data-id="element-407" data-mp="2kbope1axtfrh">
          <div className="coleccion-intro__visual coleccion-intro__visual--memoria" data-id="element-408" data-mp="2g7yy342vok2r">
            <img className="coleccion-intro__image" src="https://cdn.magicpatterns.com/uploads/oX7VMHMfxoir8RnTJneG86/ChatGPT_Image_11_jul_2026,_11_40_02.png" alt="Vela Hogar SAVIA de la colección Memoria" data-id="element-409" data-mp="2u7hxn8hmin3g" />
          </div>
          <div className="coleccion-intro__texto" data-id="element-410" data-mp="2yyq7g5mv8ah5">
            <h2 className="coleccion-intro__nombre" data-id="element-411" data-mp="2z01jstudmqjv">Memoria</h2>
            <p className="coleccion-intro__frase" data-id="element-412" data-mp="3d02m8dqai61g">
              Para los momentos que no quieres olvidar.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-413" data-mp="381j12fjisya5">
              El olfato es el único sentido que viaja directamente al recuerdo,
              sin escalas. Memoria nació de esa certeza — de que hay aromas que
              saben a casa, a abrazo, a alguien que amas. Tres fragancias para
              habitar con ternura.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-414" data-mp="3mmn4uofq1mh3">
              <div className="coleccion-intro__aroma" data-id="element-415" data-mp="3ml28ct9ujtgl">
                <span className="aroma-nombre" data-id="element-416" data-mp="38uqdimlvnglr">Hogar</span>
                <span className="aroma-notas" data-id="element-417" data-mp="38t5ikvvnhvnd">Vainilla · Almendra</span>
                <span className="aroma-desc" data-id="element-418" data-mp="3dmqi4scw6xoo">
                  Cremoso, dulce, seguro. Huele exactamente como llegar.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-419" data-mp="33vvubjjzzq14">
                <span className="aroma-nombre" data-id="element-420" data-mp="33wf4ge3epa4l">Recuerdo</span>
                <span className="aroma-notas" data-id="element-421" data-mp="2pw4c16mg7nhb">Manzanilla · Algodón</span>
                <span className="aroma-desc" data-id="element-422" data-mp="2uv7huta95xqc">
                  Limpio y tierno. Como releer una carta que guardaste mucho
                  tiempo.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-423" data-mp="voim3y1jh4uw">
                <span className="aroma-nombre" data-id="element-424" data-mp="vnqsutvezj7s">Latir</span>
                <span className="aroma-notas" data-id="element-425" data-mp="qp5yhb2uftr9">Tulipán · Vainilla suave</span>
                <span className="aroma-desc" data-id="element-426" data-mp="qn2z40fswo9v">
                  Floral suave, vivo y presente. Te recuerda que estás aquí.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`coleccion-intro ${activeIntro === 'pausa' ? 'activa' : ''}`} id="intro-pausa" data-id="element-427" data-mp="1eei98fdtk9vo">
        <div className="coleccion-intro__inner" data-id="element-428" data-mp="1ac3nnnirmw0p">
          <div className="coleccion-intro__texto" data-id="element-429" data-mp="15ebxaxxrrhym">
            <h2 className="coleccion-intro__nombre" data-id="element-430" data-mp="15dk41trn9wbi">Pausa</h2>
            <p className="coleccion-intro__frase" data-id="element-431" data-mp="3v46we62exnzd">
              El arte de no hacer nada — y que eso sea suficiente.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-432" data-mp="3q3rm5ndfgwlm">
              Vivimos en un mundo que premia la velocidad y castiga el descanso.
              Pausa existe para darte permiso. Para decirte que detenerse no es
              perder el tiempo — es recuperarte. Tres aromas que saben a
              silencio bien habitado.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-433" data-mp="2eldmzj3dnh8n">
              <div className="coleccion-intro__aroma" data-id="element-434" data-mp="2jeq65dpaevu0">
                <span className="aroma-nombre" data-id="element-435" data-mp="2od9utlv9mlq2">Ritual</span>
                <span className="aroma-notas" data-id="element-436" data-mp="2ocrqs32d337l">Palo Santo · Lavanda</span>
                <span className="aroma-desc" data-id="element-437" data-mp="2tb1oakbk6fwd">
                  Sagrado y sereno. Convierte cualquier rincón en un espacio de
                  intención.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-438" data-mp="32brg7cxp7m4t">
                <span className="aroma-nombre" data-id="element-439" data-mp="13x68ir87q72r">Descanso</span>
                <span className="aroma-notas" data-id="element-440" data-mp="13yr4mla6dy2q">Manzanilla · Lavanda</span>
                <span className="aroma-desc" data-id="element-441" data-mp="17xzz5sd1hzzc">
                  Calma herbal, noche bogotana. Permiso de soltar el día.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-442" data-mp="k3g1qlbmldcf">
                <span className="aroma-nombre" data-id="element-443" data-mp="k2n01yrixe15">Silencio</span>
                <span className="aroma-notas" data-id="element-444" data-mp="xvcpkd69h130">Algodón · Té negro</span>
                <span className="aroma-desc" data-id="element-445" data-mp="xwnuigbg0evp">
                  No es vacío — es espacio. Para cuando el mundo ha hablado
                  demasiado.
                </span>
              </div>
            </div>
          </div>
          <div className="coleccion-intro__visual coleccion-intro__visual--pausa" data-id="element-446" data-mp="14u27avrbuam">
            <img className="coleccion-intro__image" src="https://cdn.magicpatterns.com/uploads/eZs29d1Y1UcJyJeaRBLrx9/ChatGPT_Image_11_jul_2026,_11_46_53.png" alt="Vela Silencio SAVIA de la colección Pausa" data-id="element-447" data-mp="f4buw1c6brp9" />
          </div>
        </div>
      </section>

      <ColeccionesTabs data-id="element-448" data-mp="1lknu85qqffmf" />

      {/* TESTIMONIOS */}
      <section className="testimonios" data-id="element-449" data-mp="1ljvx2qhhl9u1">
        <div className="testimonios__inner" data-id="element-450" data-mp="1u1ftalf4hzin">
          <div className="testimonio__item reveal d1" data-id="element-451" data-mp="1u0mvi9y56kcn">
            <p className="testimonio__quote" data-id="element-452" data-mp="1y2rlmypdsyuj">
              Tierra es la vela que siempre había buscado. La uso en mis clases
              de yoga y mis estudiantes siempre preguntan qué es ese aroma.
            </p>
            <span className="testimonio__firma" data-id="element-453" data-mp="1p1jncgmv7y0g">— Carolina V., Cali</span>
          </div>
          <div className="testimonio__item reveal d2" data-id="element-454" data-mp="2crdz0uvg7gj4">
            <p className="testimonio__quote" data-id="element-455" data-mp="2cspc5lasa0mw">
              Pedí el Ritual SAVIA como regalo y terminé quedándomelo. El
              pebetero y los fragmentos cambiaron completamente el ambiente de
              mi cuarto.
            </p>
            <span className="testimonio__firma" data-id="element-456" data-mp="280xiv308tgt2">— Manuela R., Bogotá</span>
          </div>
          <div className="testimonio__item reveal d3" data-id="element-457" data-mp="2vqruxicqn1c9">
            <p className="testimonio__quote" data-id="element-458" data-mp="2vp75261dc6kq">
              Silencio es exactamente lo que su nombre promete. La enciendo
              cuando llego a casa y algo en mí sabe que ya llegué.
            </p>
            <span className="testimonio__firma" data-id="element-459" data-mp="vy04civqmcpz">— Daniela M., Medellín</span>
          </div>
        </div>
      </section>

      {/* MÁS ALLÁ */}
      <section className="mas-alla" id="mas-alla" data-id="element-460" data-mp="m7n83tek8v40">
        <div className="savia-container" data-id="element-461" data-mp="m755mf1b1knn">
          <div className="mas-alla__header reveal" data-id="element-462" data-mp="r68ds8cl06zu">
            <p className="mas-alla__overline" data-id="element-463" data-mp="r4x5pwc3sd2x">Más allá de las velas</p>

            <h2 className="mas-alla__title" data-id="element-464" data-mp="c8673pa975ed">
              Kits & objetos
              <br data-id="element-465" data-mp="h82e7xe8zcy5" />
              <em data-id="element-466" data-mp="h8kdl2w7i7aa">para regalar o habitar.</em>
            </h2>
          </div>

          <div className="mas-alla__grid" id="mas-alla-grid" data-id="element-467" data-mp="38svor2kk6r0">
            <div className="product-card reveal d1" data-id="element-468" data-mp="3am4abwqt11n">
              <div className="product-card__image" data-id="element-469" data-mp="86ufxfbqec42">
                <img src="https://cdn.magicpatterns.com/uploads/hfNXz1KRRNk79LtbWbQMLS/FRAGMENTOS.png" alt="Fragmentos Aromáticos SAVIA" data-id="element-470" data-mp="89gwu5kii3yy" />
              </div>

              <div className="product-card__info" data-id="element-471" data-mp="2cpliaaic2bah">
                <div className="product-card__top" data-id="element-472" data-mp="2cqdg7rzekl3x">
                  <h3 className="product-card__nombre" data-id="element-473" data-mp="27qh6rd7xs1gz">
                    Fragmentos Aromáticos
                  </h3>
                  <p className="product-card__notas" data-id="element-474" data-mp="2li5ktm1qx00l">Pack x10 · 130g aprox.</p>
                  <div className="aroma-selector" data-id="element-475" data-mp="2grffg8tbkx5w">
                    <label className="aroma-selector__label" htmlFor="fragmentos-aroma" data-id="element-476" data-mp="2gpm8stipicxw">
                      Elige tu aroma
                    </label>
                    <select id="fragmentos-aroma" className={`aroma-selector__select${fragmentosAromaError ? ' aroma-selector__select--error' : ''}`} value={fragmentosAroma} aria-invalid={fragmentosAromaError} onChange={event => {
                    setFragmentosAroma(event.target.value);
                    setFragmentosAromaError(false);
                  }} required data-id="element-477" data-mp="2qj2zhonjrbxc">
                      <option value="" disabled data-id="element-478" data-mp="3a4ghgtmgzuci">
                        — seleccionar —
                      </option>
                      <optgroup label="ORIGEN" data-id="element-479" data-mp="3f31fcme921xs">
                        <option value="Tierra · Sándalo + Palo Santo" data-id="element-480" data-mp="3f6fkg2easp8x">
                          Tierra · Sándalo + Palo Santo
                        </option>
                        <option value="Aire · Bergamota + Té verde" data-id="element-481" data-mp="3o3zofj3jozn8">
                          Aire · Bergamota + Té verde
                        </option>
                        <option value="Agua · Acorde marino + Eucalipto" data-id="element-482" data-mp="1pgblqu7xsiv">
                          Agua · Acorde marino + Eucalipto
                        </option>
                      </optgroup>
                      <optgroup label="MEMORIA" data-id="element-483" data-mp="bnm9lxcwmeig">
                        <option value="Hogar · Vainilla + Almendra" data-id="element-484" data-mp="boxfq3ttnycq">
                          Hogar · Vainilla + Almendra
                        </option>
                        <option value="Recuerdo · Manzanilla + Algodón" data-id="element-485" data-mp="207i0yxzznuo0">
                          Recuerdo · Manzanilla + Algodón
                        </option>
                        <option value="Latir · Tulipán + Vainilla suave" data-id="element-486" data-mp="1w7hovk0d3vk4">
                          Latir · Tulipán + Vainilla suave
                        </option>
                      </optgroup>
                      <optgroup label="PAUSA" data-id="element-487" data-mp="1m9bpb92131if">
                        <option value="Ritual · Palo Santo + Lavanda" data-id="element-488" data-mp="2jv916nlwk8le">
                          Ritual · Palo Santo + Lavanda
                        </option>
                        <option value="Descanso · Manzanilla + Lavanda" data-id="element-489" data-mp="2ey8ucah8b1t8">
                          Descanso · Manzanilla + Lavanda
                        </option>
                        <option value="Silencio · Algodón + Té negro" data-id="element-490" data-mp="255jw4haovkfu">
                          Silencio · Algodón + Té negro
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="product-card__footer" data-id="element-491" data-mp="2ic7nsplbvzxa">
                  <span className="product-card__precio" data-id="element-492" data-mp="2ietuyo4b4dez">$42.000</span>
                  <button type="button" className="product-card__cta" onClick={addFragmentos} aria-label="Agregar Fragmentos Aromáticos al carrito" data-id="element-493" data-mp="2mf3yzv8jgtr7">
                    Agregar
                    <ArrowRightIcon size={12} strokeWidth={1.5} aria-hidden="true" data-id="element-494" data-mp="312yshdudf25c" />
                  </button>
                </div>
              </div>
            </div>

            <div className="product-card product-card--coming-soon reveal d2" data-id="element-495" data-mp="2rbb5pq8re9a7">
              <div className="product-card__image" data-id="element-496" data-mp="2wanu4ffa3oew">
                <div className="product-card__placeholder" data-id="element-497" data-mp="3k1ktyttsuv9s"></div>
                <div className="product-card__coming-soon" aria-hidden="true" data-id="element-498" data-mp="3k09s4zjwzxlb">
                  <span data-id="element-499" data-mp="3ou493v84xygp">Próximamente</span>
                </div>
              </div>
              <div className="product-card__info" data-id="element-500" data-mp="1dcj6h1c1fq9t">
                <h3 className="product-card__nombre" data-id="element-501" data-mp="18er7jncz73w4">Presencia I</h3>
                <p className="product-card__notas" data-id="element-502" data-mp="18ehfzvca8oob">8 cm · 115g</p>
                <div className="product-card__footer" data-id="element-503" data-mp="1m52z9l5nf0mq">
                  <span className="product-card__precio product-card__precio--soon" data-id="element-504" data-mp="1i4j0ye9c9qz0">
                    Disponible pronto
                  </span>
                  <a className="product-card__cta product-card__cta--disabled" aria-disabled="true" tabIndex={-1} onClick={event => event.preventDefault()} data-id="element-505" data-mp="ufrg67y2waw9">
                    Pedir
                    <ArrowRightIcon size={12} strokeWidth={1.5} data-id="element-506" data-mp="yga801zz1gyg" />
                  </a>
                </div>
              </div>
            </div>

            <div className="product-card product-card--coming-soon reveal d3" data-id="element-507" data-mp="2ewwf3bhspk3e">
              <div className="product-card__image" data-id="element-508" data-mp="29v6zrjnjik9i">
                <div className="product-card__placeholder" data-id="element-509" data-mp="17ikic659y9mt"></div>
                <div className="product-card__coming-soon" aria-hidden="true" data-id="element-510" data-mp="17ketwtunope8">
                  <span data-id="element-511" data-mp="1cfu85mwksnb6">Próximamente</span>
                </div>
              </div>
              <div className="product-card__info" data-id="element-512" data-mp="1gk1vpgrarx7t">
                <h3 className="product-card__nombre" data-id="element-513" data-mp="1li3fy50gm2kv">Presencia II</h3>
                <p className="product-card__notas" data-id="element-514" data-mp="1lhai5tjhanev">12 cm · 115g</p>
                <div className="product-card__footer" data-id="element-515" data-mp="osug4xz6mlkv">
                  <span className="product-card__precio product-card__precio--soon" data-id="element-516" data-mp="opzyfe7jzev5">
                    Disponible pronto
                  </span>
                  <a className="product-card__cta product-card__cta--disabled" aria-disabled="true" tabIndex={-1} onClick={event => event.preventDefault()} data-id="element-517" data-mp="xrg4xav8ywu3">
                    Pedir
                    <ArrowRightIcon size={12} strokeWidth={1.5} data-id="element-518" data-mp="a252zq25uwed" />
                  </a>
                </div>
              </div>
            </div>

            <div className="product-card reveal d4" data-id="element-519" data-mp="2ima8s347osl">
              <div className="product-card__image" data-id="element-520" data-mp="3tpfe63pam20t">
                <img src="https://cdn.magicpatterns.com/uploads/cmLn7L7Ls22KEHrjLjDVm7/ChatGPT_Image_11_jul_2026,_00_05_12.png" alt="Ritual SAVIA con pebetero y fragmentos aromáticos" data-id="element-521" data-mp="3tnm74narpfsa" />
              </div>
              <div className="product-card__info" data-id="element-522" data-mp="3fqz4q501pxez">
                <div className="product-card__top" data-id="element-523" data-mp="3frqomika66pf">
                  <h3 className="product-card__nombre" data-id="element-524" data-mp="3ayfhshfgxpxi">Ritual SAVIA</h3>
                  <p className="product-card__notas" data-id="element-525" data-mp="35zl1pn8vyfh3">
                    Pebetero + Pack x10 fragmentos · Aroma a elección
                  </p>
                  <div className="aroma-selector" data-id="element-526" data-mp="312umf24wnnwq">
                    <label className="aroma-selector__label" htmlFor="ritual-savia-aroma" data-id="element-527" data-mp="2wzxx6skv0tjt">
                      Elige tu aroma
                    </label>
                    <select id="ritual-savia-aroma" className={`aroma-selector__select${ritualAromaError ? ' aroma-selector__select--error' : ''}`} value={ritualAroma} aria-invalid={ritualAromaError} onChange={event => {
                    setRitualAroma(event.target.value);
                    setRitualAromaError(false);
                  }} required data-id="element-528" data-mp="jsoj3udfiqp2">
                      <option value="" disabled data-id="element-529" data-mp="5t6srakhj5dl">
                        — seleccionar —
                      </option>
                      <optgroup label="ORIGEN" data-id="element-530" data-mp="3n6ixf2nrbbar">
                        <option value="Tierra · Sándalo + Palo Santo" data-id="element-531" data-mp="3s8i3wlesmobx">
                          Tierra · Sándalo + Palo Santo
                        </option>
                        <option value="Aire · Bergamota + Té verde" data-id="element-532" data-mp="3j6sbbc96djck">
                          Aire · Bergamota + Té verde
                        </option>
                        <option value="Agua · Acorde marino + Eucalipto" data-id="element-533" data-mp="2zlidjgj8ewkd">
                          Agua · Acorde marino + Eucalipto
                        </option>
                      </optgroup>
                      <optgroup label="MEMORIA" data-id="element-534" data-mp="38mgaxfz605tz">
                        <option value="Hogar · Vainilla + Almendra" data-id="element-535" data-mp="34kbg4dwzd35r">
                          Hogar · Vainilla + Almendra
                        </option>
                        <option value="Recuerdo · Manzanilla + Algodón" data-id="element-536" data-mp="3ii9tbfzketjo">
                          Recuerdo · Manzanilla + Algodón
                        </option>
                        <option value="Latir · Tulipán + Vainilla suave" data-id="element-537" data-mp="3s9xdcvtsl8b4">
                          Latir · Tulipán + Vainilla suave
                        </option>
                      </optgroup>
                      <optgroup label="PAUSA" data-id="element-538" data-mp="546ggn7rwbff">
                        <option value="Ritual · Palo Santo + Lavanda" data-id="element-539" data-mp="52vconebv3ob">
                          Ritual · Palo Santo + Lavanda
                        </option>
                        <option value="Descanso · Manzanilla + Lavanda" data-id="element-540" data-mp="12h04asi0iba6">
                          Descanso · Manzanilla + Lavanda
                        </option>
                        <option value="Silencio · Algodón + Té negro" data-id="element-541" data-mp="sjvf0jsvdzp5">
                          Silencio · Algodón + Té negro
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="product-card__footer" data-id="element-542" data-mp="jki4rowx973d">
                  <span className="product-card__precio" data-id="element-543" data-mp="erxgf2wsbm78">$98.000</span>
                  <button type="button" className="product-card__cta" onClick={addRitualSavia} aria-label="Agregar Ritual SAVIA al carrito" data-id="element-544" data-mp="9tdpsp78xm8j">
                    Agregar
                    <ArrowRightIcon size={12} strokeWidth={1.5} aria-hidden="true" data-id="element-545" data-mp="3vzsrx0if1lr6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOSOTROS */}
      <section className="nosotros" id="nosotros" data-id="element-546" data-mp="2bke72xcokmeg">
        <div className="savia-container" data-id="element-547" data-mp="2gio5rhxm654t">
          <div className="nosotros__grid" data-id="element-548" data-mp="2gfjvc2tk2d5p">
            <div className="nosotros__visual reveal-left" data-id="element-549" data-mp="2lgzf7rjzydmi">
              <img src="https://cdn.magicpatterns.com/uploads/vvTWywxkYpg18yqapKCtsi/ChatGPT_Image_11_jun_2026,_10_56_01.png" alt="Juan y Alejandra elaborando velas y fragmentos SAVIA a mano" data-id="element-550" data-mp="2led5pmdjpo1n" />
            </div>

            <div className="reveal-right" data-id="element-551" data-mp="1xqd6rz62yrbz">
              <p className="nosotros__overline" data-id="element-552" data-mp="1xpbw3vsz86us">La historia detrás</p>
              <h2 className="nosotros__title" data-id="element-553" data-mp="22j69yilwhrly">
                SAVIA nació de
                <br data-id="element-554" data-mp="151od2l7ctoa9" />
                <em data-id="element-555" data-mp="1527k36vgus9i">un reencuentro.</em>
              </h2>
              <p className="nosotros__texto" data-id="element-556" data-mp="1a0slvaqd9k02">
                Juan y Alejandra se reencontraron y nació el deseo de habitar
                juntos. En ese trayecto se dieron cuenta de algo que parece
                simple pero cambia todo: los espacios necesitan sentirse de una
                manera más agradable, más propia, más presente.
              </p>
              <p className="nosotros__texto" data-id="element-557" data-mp="1bsij9xdfneoj">
                El aroma fue la respuesta. No porque sea decoración, sino porque
                es el único lenguaje que va directo al cuerpo — sin pasar por la
                mente, sin pedir permiso.
              </p>
              <p className="nosotros__texto" data-id="element-558" data-mp="f1zvitz0o5vm">
                Así nació SAVIA. Dos personas. Un estudio en Bogotá. Cera de
                soya que se vierte a mano. Y la convicción de que la forma en
                que huele tu casa determina cómo te sientes en ella.
              </p>

              <p className="nosotros__firma" data-id="element-559" data-mp="24e1xmntc0u7q">
                — Juan & Alejandra · Bogotá, Colombia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILARES + ENVÍOS */}
      <section className="pilares" data-id="element-560" data-mp="3hcbzjvlyboy7">
        <div className="savia-container--wide" data-id="element-561" data-mp="3mcyk34frk26w">
          <div className="pilares__grid" data-id="element-562" data-mp="3mdrklno4pvgl">
            <div className="pilar reveal d1" data-id="element-563" data-mp="3malwaanbqxlx">
              <div className="pilar__num" data-id="element-564" data-mp="38e8mzosy22ij">01</div>
              <div className="pilar__icon" data-id="element-565" data-mp="38fjsbt21fibr">
                <LeafIcon size={18} strokeWidth={1.2} data-id="element-566" data-mp="3ddbj2jqy4yed" />
              </div>
              <h3 className="pilar__titulo" data-id="element-567" data-mp="4wqdze1fqpgj">Cera de soya 100% vegetal</h3>
              <p className="pilar__texto" data-id="element-568" data-mp="4yuj1zb2luie">
                Sin parafina. Sin derivados del petróleo. Sin tóxicos. Lo que
                entra al aire que respiras importa.
              </p>
            </div>
            <div className="pilar reveal d2" data-id="element-569" data-mp="5rzmk00c1fx">
              <div className="pilar__num" data-id="element-570" data-mp="4gvgj2ngroa">02</div>
              <div className="pilar__icon" data-id="element-571" data-mp="nvcjd53zj2q9">
                <HandIcon size={18} strokeWidth={1.2} data-id="element-572" data-mp="nub56rroa04b" />
              </div>
              <h3 className="pilar__titulo" data-id="element-573" data-mp="smkt62988q3y">Hecha a mano en Bogotá</h3>
              <p className="pilar__texto" data-id="element-574" data-mp="snvy45ees3wn">
                Cada vela vertida y terminada a mano. Sin producción industrial.
                Con presencia real en cada pieza.
              </p>
            </div>
            <div className="pilar reveal d3" data-id="element-575" data-mp="2r4y0d6qc7lzo">
              <div className="pilar__num" data-id="element-576" data-mp="2r5qwzevl0v43">03</div>
              <div className="pilar__icon" data-id="element-577" data-mp="2m5uu5iymf9q4">
                <WindIcon size={18} strokeWidth={1.2} data-id="element-578" data-mp="2m768gcpozzvh" />
              </div>
              <h3 className="pilar__titulo" data-id="element-579" data-mp="3iwn9ufhn6zrd">Aromas con notas reales</h3>
              <p className="pilar__texto" data-id="element-580" data-mp="3ixyhiqe7krnr">
                Sándalo, palo santo, bergamota, tulipán, lavanda. Fragancias
                construidas desde la emoción.
              </p>
            </div>
            <div className="pilar reveal d4" data-id="element-581" data-mp="354qmeoqx5e1b">
              <div className="pilar__num" data-id="element-582" data-mp="353ntkba1d1ir">04</div>
              <div className="pilar__icon" data-id="element-583" data-mp="6eqgb7kzubmx">
                <PackageIcon size={18} strokeWidth={1.2} data-id="element-584" data-mp="6fs2a73jsijf" />
              </div>
              <h3 className="pilar__titulo" data-id="element-585" data-mp="1ngrg979rcc3">Packaging consciente</h3>
              <p className="pilar__texto" data-id="element-586" data-mp="1m5na89ww2kg">
                Kraft natural, papel mantequilla, pita de algodón. Todo
                reciclable. El cuidado no termina en la vela.
              </p>
            </div>
          </div>
        </div>

        <div className="envios-strip" data-id="element-587" data-mp="ix2tyj1nociw">
          <div className="envio-item reveal d1" data-id="element-588" data-mp="iw1bhtib8nr5">
            <div className="envio-item__icon" data-id="element-589" data-mp="mzfrwla5v654">
              <ClockIcon size={20} strokeWidth={1.2} data-id="element-590" data-mp="n08wpgpk7lkm" />
            </div>
            <div data-id="element-591" data-mp="rx7s02kylyke">
              <p className="envio-item__titulo" data-id="element-592" data-mp="rxqymn55t0j4">Producción bajo pedido</p>
              <p className="envio-item__texto" data-id="element-593" data-mp="ww2b75mu9p51">
                Cada vela se hace especialmente para ti. Despacho en 2–4 días
                hábiles desde Bogotá.
              </p>
            </div>
          </div>
          <div className="envio-item reveal d2" data-id="element-594" data-mp="16mdovjwbrcp4">
            <div className="envio-item__icon" data-id="element-595" data-mp="1aos7c2w30afv">
              <MapPinIcon size={20} strokeWidth={1.2} data-id="element-596" data-mp="1aq3bw4xcpm81" />
            </div>
            <div data-id="element-597" data-mp="1fmtsctd2ijtz">
              <p className="envio-item__titulo" data-id="element-598" data-mp="1fod9em6jsywt">Envíos a toda Colombia</p>
              <p className="envio-item__texto" data-id="element-599" data-mp="3bpygo04vsjzs">
                Bogotá, Medellín, Cali, Barranquilla y todo el territorio.
                Empaque protegido.
              </p>
            </div>
          </div>
          <div className="envio-item reveal d3" data-id="element-600" data-mp="3lgt77gp5u5r1">
            <div className="envio-item__icon" data-id="element-601" data-mp="3lidymxg6h8ko">
              <ShieldCheckIcon size={20} strokeWidth={1.2} data-id="element-602" data-mp="3gpkwmhc3geca" />
            </div>
            <div data-id="element-603" data-mp="3veh2c7qohf9q">
              <p className="envio-item__titulo" data-id="element-604" data-mp="3vdxph5fvxh2k">Pago seguro</p>
              <p className="envio-item__texto" data-id="element-605" data-mp="3qfmjj5spnqpm">
                MercadoPago · PSE · Contraentrega disponible en Colombia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="cta-final" id="contacto" data-id="element-606" data-mp="3aeo73c1b5ls">
        <div className="savia-container--narrow" data-id="element-607" data-mp="38tvlj9a5wqk">
          <p className="cta-final__overline reveal" data-id="element-608" data-mp="2a0kysyn1s07g">Una sola decisión</p>
          <h2 className="cta-final__title reveal d1" data-id="element-609" data-mp="252b4eq95d3mw">
            Llevas años construyendo el espacio perfecto. Ya es hora de que ese
            espacio <em data-id="element-610" data-mp="klk0qm1hhklx">también te cuide a ti.</em>
          </h2>
          <p className="cta-final__sub reveal d2" data-id="element-611" data-mp="pj2bmhz2a9wk">
            No necesitas una casa más grande. Solo necesitas un momento, una
            llama y una intención.
          </p>
          <div className="cta-final__botones reveal d3" data-id="element-612" data-mp="bpugigbruwa4">
            <Link to="#colecciones" className="btn-primary" data-id="element-613" data-mp="fsh5m9h2633m">
              <ArrowRightIcon size={14} strokeWidth={1.5} data-id="element-614" data-mp="1u0y4gbdg8kf" />
              Ver colecciones
            </Link>
            <a href="#mas-alla" className="btn-outline" onClick={e => {
            e.preventDefault();
            const target = document.getElementById('mas-alla');
            const nav = document.getElementById('nav');
            if (target) {
              const offset = (nav?.offsetHeight ?? 0) + 24;
              const top = target.getBoundingClientRect().top + window.scrollY - offset + 120;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} data-id="element-615" data-mp="6skiwde8beb7">
              Ritual SAVIA
            </a>
          </div>
          <div className="cta-final__divider reveal d4" data-id="element-616" data-mp="5769zri9fgl8"></div>
          <p className="cta-final__instagram reveal d4" data-id="element-617" data-mp="9o6ign0htkm">
            ¿Tienes dudas? Escríbenos:{' '}
            <a href="https://wa.me/573213396782?text=Hola%20SAVIA" target="_blank" rel="noopener noreferrer" data-id="element-618" data-mp="3sftiuscyp2od">
              WhatsApp
            </a>{' '}
            · Instagram:{' '}
            <a href="https://instagram.com/savia.ritualco" target="_blank" rel="noopener noreferrer" data-id="element-619" data-mp="3dgzgkyh1e3cm">
              @savia.ritualco
            </a>
          </p>
        </div>
      </section>
      <QuizOverlay isOpen={isQuizOpen} onClose={closeQuiz} data-id="element-620" data-mp="1bqo1xl4nooiv" />
    </>;
}